import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import { MapPin, Users, ImageIcon } from 'lucide-react';

// Posities (in % van de kaartafbeelding) van steden verspreid over Nederland,
// om de landelijke beschikbaarheid te benadrukken
const dekkingsStippen = [
  { name: 'Groningen', x: 68, y: 21.5 },
  { name: 'Leeuwarden', x: 56, y: 22 },
  { name: 'Assen', x: 68, y: 27 },
  { name: 'Emmen', x: 73, y: 32 },
  { name: 'Zwolle', x: 61.5, y: 39.5 },
  { name: 'Enschede', x: 72, y: 46 },
  { name: 'Alkmaar', x: 40.5, y: 36 },
  { name: 'Haarlem', x: 40, y: 42 },
  { name: 'Amsterdam', x: 42.5, y: 43 },
  { name: 'Utrecht', x: 45, y: 49.5 },
  { name: 'Apeldoorn', x: 58.5, y: 46.5 },
  { name: 'Arnhem', x: 57.5, y: 52 },
  { name: 'Den Haag', x: 34, y: 50 },
  { name: 'Rotterdam', x: 36.5, y: 54 },
  { name: 'Middelburg', x: 26.5, y: 66.5 },
  { name: 'Breda', x: 41, y: 64 },
  { name: "'s-Hertogenbosch", x: 48, y: 59 },
  { name: 'Eindhoven', x: 51, y: 65.5 },
  { name: 'Maastricht', x: 55, y: 80 },
];

const teamMembers = [
  {
    name: 'Erik',
    role: 'Vertrouwenspersoon',
    bio: 'LVV-gecertificeerd vertrouwenspersoon, woonachtig in Den Haag met een passie voor reizen.',
    image: '/images/team/erik.webp',
  },
  {
    name: 'Charlotte',
    role: 'Vertrouwenspersoon',
    bio: 'LVV-gecertificeerd vertrouwenspersoon, woonachtig in Haarlem met een passie voor kiten.',
    image: '/images/team/charlotte.webp',
  },
  {
    name: 'Eva',
    role: 'Vertrouwenspersoon',
    bio: 'LVV-gecertificeerd vertrouwenspersoon, woonachtig in Amsterdam met een passie voor yoga.',
    image: '/images/team/eva.webp?v=3',
  },
  {
    name: 'Paul',
    role: 'Founder',
    bio: 'Founder van PuntUit, woonachtig in Haarlem met een passie voor tennis.',
    image: '/images/team/paul.webp',
  },
];

export function OverOns() {
  return (
    <>
      <SEO title="Over Ons" description="Maak kennis met het team van PuntUit: LVV-gecertificeerde externe vertrouwenspersonen met vestigingen in Diemen en Den Haag, actief door heel Nederland." canonicalUrl="/over-ons" />
      <SchemaMarkup
        type="webpage"
        pageUrl="https://puntuit.nl/over-ons"
        pageTitle="Over Ons — PuntUit"
        pageDescription="Maak kennis met het team van PuntUit: LVV-gecertificeerde externe vertrouwenspersonen met vestigingen in Diemen en Den Haag, actief door heel Nederland."
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Over Ons', url: '/over-ons' },
        ]}
      />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Mensen die begrijpen wat er speelt.</h1>
            <p className="text-xl text-primary-100">PuntUit bestaat uit LVV-gecertificeerde vertrouwenspersonen. Geen bureau. Geen anoniem loket. Echte mensen met echte expertise.</p>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container max-w-5xl">
            <div className="text-center mb-12">
              <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Het team</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">De mensen achter PuntUit.</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Aandacht en zorg, dat is wat ons motiveert. Wij zijn er voor uw werknemers en dus voor u.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {teamMembers.map((member) => (
                <div key={member.name} className="text-center">
                  <div className="w-36 h-36 rounded-full mx-auto mb-5 overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                    {member.image ? (
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-10 h-10 text-gray-300" />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-[#0284c7] font-medium text-sm mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container max-w-3xl">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-4">Waar PuntUit voor staat</p>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>PuntUit helpt organisaties om sociale veiligheid niet alleen goed te regelen, maar ook actief levend te houden. Dat begint bij bewustwording en preventie. Niet pas op het moment dat een situatie escaleert, maar juist eerder, als kleine signalen nog klein zijn.</p>
              <p>Daarom biedt PuntUit meer dan alleen een externe vertrouwenspersoon. Organisaties krijgen toegang tot LVV-gecertificeerde registervertrouwenspersonen, een kosteloos online spreekuur, een zelfhulpplatform, periodieke awareness-mails, het PuntUit-spel en de Early Awareness Tool. Samen vormen die onderdelen een aanpak die helpt om omgangsvormen bespreekbaar te maken, mensen eerder in beweging te krijgen en problemen vroeg op te pakken.</p>
            </div>
            <blockquote className="mt-8 border-l-4 border-l-[#E85D56] pl-6 py-2">
              <p className="text-xl font-semibold text-gray-900 leading-relaxed">De overtuiging achter PuntUit is helder: de beste vertrouwenspersoon is de vertrouwenspersoon die je niet nodig hebt.</p>
              <p className="text-gray-700 mt-3">Door vroegtijdig aandacht te geven aan wat er speelt, helpt PuntUit om problemen te voorkomen waar dat kan en goed te begeleiden waar dat nodig is.</p>
            </blockquote>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container max-w-5xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0284c7]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#0284c7]" />
                  </div>
                  <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em]">Landelijke dekking</p>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Overal in Nederland beschikbaar.</h2>
                <p className="text-gray-700 mb-4">PuntUit biedt landelijke dekking. Of je nu in Amsterdam, Maastricht of Groningen zit — wij zijn er voor jouw organisatie.</p>
                <p className="text-gray-700 mb-4">Gesprekken kunnen online, telefonisch of op een neutrale locatie in de buurt plaatsvinden — nooit op locatie van de organisatie, uit oogpunt van vertrouwelijkheid.</p>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 mt-6">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-[#0284c7] mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Neutraal en vertrouwelijk</p>
                      <p className="text-gray-600 text-sm">We spreken af op een neutrale locatie in de buurt, online of telefonisch. Medewerkers kiezen zelf wat het prettigst voelt.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl aspect-square overflow-hidden relative">
                <img
                  src="/images/120923-05.jpg"
                  alt="Kaart van Nederland met stippen die de landelijke beschikbaarheid van PuntUit tonen"
                  className="w-full h-full object-cover"
                />
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
                  {dekkingsStippen.map((stip) => (
                    <g key={stip.name}>
                      <circle cx={stip.x} cy={stip.y} r="2.4" fill="#E85D56" opacity="0.2" />
                      <circle cx={stip.x} cy={stip.y} r="1.1" fill="#E85D56" />
                    </g>
                  ))}
                </svg>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-[#0284c7] text-white">
          <div className="container text-center max-w-3xl">
            <h2 className="text-3xl font-bold mb-4">Maak kennis met ons team.</h2>
            <p className="text-white/80 text-lg mb-8">Vraag een vrijblijvend kennismakingsgesprek aan. Wij vertellen je graag meer over onze aanpak.</p>
            <Link to="/contact" className="bg-white text-[#0284c7] px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 hover:bg-gray-50 transition-colors">Neem contact op</Link>
          </div>
        </section>
      </div>
    </>
  );
}
